import React, { useEffect, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputProps,
  View,
  ViewStyle,
} from 'react-native';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isIOS, isWeb } from 'shared/constants/platform';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

import { getChatTextFieldStyles } from './ChatTextField.helpers';

export interface ChatTextFieldProps
  extends TextInputProps,
    PressableAnalyticsProps {
  inputTextFieldLabel: string | null;
  inputTextFieldError?: boolean;
  inputTextFieldAssistiveText?: string | null;
  inputIcon?: React.ReactNode;
  assistiveTextIcon?: React.ReactNode;
}

const MAX_HEIGHT = 160;
const MIN_HEIGHT = 46;

const ChatTextField = ({
  inputTextFieldLabel,
  inputTextFieldError = false,
  inputTextFieldAssistiveText,
  'aria-label': ariaLabel,
  autoFocus,
  readOnly = false,
  value,
  style,
  onBlur,
  onFocus,
  onChangeText,
  analyticsId = '',
  customAttributes = null,
  inputIcon,
  assistiveTextIcon,
  ...rest
}: ChatTextFieldProps) => {
  const textFieldAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes: { ...customAttributes },
  });
  const userAgent = navigator.userAgent;

  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<TextInput>(null);

  const { borderColor, textColor: finalTextColor } = getChatTextFieldStyles(
    inputTextFieldError,
    isFocused,
    readOnly,
    !!rest.placeholder,
  );

  const assistiveTextColor = inputTextFieldError
    ? theme.color.status.negative
    : theme.color.text.secondary;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (autoFocus && !readOnly) {
      setIsFocused(true);
      timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [autoFocus, readOnly]);

  const [inputHeight, setInputHeight] = useState(MIN_HEIGHT);

  const handleContentSizeChange = (
    event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ) => {
    if (!isIOS && !isWeb) {
      const { height } = event.nativeEvent.contentSize;

      setInputHeight(Math.max(MIN_HEIGHT, Math.min(height, MAX_HEIGHT)));
    }
  };

  const handleChange = (text: string) => {
    const formattedText = text;
    onChangeText?.(formattedText);
  };

  const inputTextVerticalPadding =
    userAgent?.includes('Chrome') || userAgent?.includes('Safari') ? 0 : 12;

  return (
    <View style={[style as ViewStyle, styles.inputWrapper]}>
      {inputTextFieldLabel ? (
        <BodySmBoldText
          style={styles.inputTextFieldLabel}
          aria-label={`Label for ${inputTextFieldLabel}`}
          id={inputTextFieldLabel}
        >
          {inputTextFieldLabel}
        </BodySmBoldText>
      ) : null}
      <View
        style={[styles.input, { borderColor: borderColor }]}
        testID={`text-input-container-${inputTextFieldLabel}`}
      >
        <View style={styles.innerContainer}>
          <TextInput
            style={[
              styles.inputText,
              {
                color: finalTextColor,
                paddingVertical: inputTextVerticalPadding,
                minHeight: isWeb ? MIN_HEIGHT : inputHeight,
                maxHeight: inputHeight,
              },
            ]}
            ref={inputRef}
            onBlur={(event) => {
              setIsFocused(false);
              onBlur?.(event);
            }}
            onFocus={(event) => {
              setIsFocused(true);
              textFieldAnalytics();
              onFocus?.(event);
            }}
            multiline
            aria-labelledby={inputTextFieldLabel ?? ''}
            aria-label={inputTextFieldLabel ?? ''}
            aria-disabled={readOnly}
            value={value}
            onChangeText={handleChange}
            onContentSizeChange={handleContentSizeChange}
            numberOfLines={Math.floor(inputHeight / 20)}
            scrollEnabled={inputHeight >= MAX_HEIGHT}
            testID={`text-input-${inputTextFieldLabel}`}
            accessible
            {...rest}
          />
          {inputIcon ? (
            <View style={styles.iconContainer}>{inputIcon}</View>
          ) : null}
        </View>
      </View>

      {inputTextFieldAssistiveText ? (
        <View style={styles.inputAssistiveTextContainer}>
          {assistiveTextIcon && inputTextFieldError ? assistiveTextIcon : null}
          <BodyXSText
            style={[
              styles.inputTextFieldAssistiveText,
              {
                color: assistiveTextColor,
              },
            ]}
          >
            {inputTextFieldAssistiveText}
          </BodyXSText>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    alignItems: 'center',
  },
  innerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  input: {
    alignContent: 'center',
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: theme.spacing.m,
  },
  inputAssistiveTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  inputText: {
    alignContent: 'center',
    flexGrow: 1,
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
    fontSize: 16,
    letterSpacing: 0.02,
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineWidth: 0,
        alignItems: 'stretch',
      },
    }),
  },
  inputTextFieldAssistiveText: {
    color: theme.color.text.secondary,
    display: 'flex',
  },
  inputTextFieldLabel: {
    color: theme.color.text.primary,
  },
  inputWrapper: {
    display: 'flex',
    gap: 4,
  },
});

export default ChatTextField;
